import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
  Modal,
  Badge,
} from 'react-bootstrap';
import { FiMapPin, FiEdit2, FiFileText, FiPlus, FiSlash, FiCheckCircle, FiEye, FiCheckSquare } from 'react-icons/fi';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import StatusPill from '@core/components/StatusPill';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import PaginationBar from '@core/components/PaginationBar';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import TablePaginationFooter, { getPaginationRange } from '@core/components/TablePaginationFooter';
import { useSearchUrlSync } from '@core/hooks/useSearchUrlSync';
import { useDebounce } from '@core/hooks/useDebounce';
import { useBulkSelection } from '@core/hooks/useBulkSelection';
import { buildDefaultListParams } from '@core/utils/listQuery';
import {
  NAME_SORT_FILTER_FIELD,
  SORT_BY_CHURCH_NAME,
  SORT_BY_DISTRICT_NAME,
  SORT_DIR_ASC,
  countContentFilters,
  normalizeSortDir,
  readSortDirFromSearchParams,
  withCatalogSort,
} from '@core/constants/listSort';
import { createChurch, updateChurch } from '../api/churchApi';
import { useDistricts } from '../../district/hooks/useDistricts';
import { useChurches } from '../hooks/useChurches';
import { useChurchActions } from '../hooks/useChurchActions';
import { ChurchQueryParams, Church } from '../types/church';
import { useLocation, useSearchParams } from 'react-router-dom';
import PageBreadcrumb from '@core/components/PageBreadcrumb';
import { useEntityBasePath } from '@core/hooks/useEntityBasePath';
import { districtsToFilteredChurchesTrail } from '@core/navigation/pageBreadcrumbTrails';
import ChurchTemplatesModal from '@features/church/components/ChurchTemplatesModal';
import BulkAssignTemplatesModal from '@features/church/components/BulkAssignTemplatesModal';
import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';
import StatusChangeConfirmModal, { StatusChangeKind } from '@features/admin/finance/components/StatusChangeConfirmModal';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES, BULK_TEMPLATE_ASSIGN_CODES } from '@features/entity/types/actionCodes';
import { useErrorModalStore } from '@infrastructure/api/store/errorModalStore';
import { ErrorResponse } from '@core/types/errorResponse';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import { useApiError } from '@infrastructure/api/hooks/useApiError';

type CreateChurchFormErrors = Partial<Record<'church_name' | 'district_id' | 'description', string[]>>;

const CHURCH_ACTION_CODES = [
  ACTION_CODES.CREATE_CHURCH,
  ACTION_CODES.ENABLE_CHURCH,
  ACTION_CODES.DISABLE_CHURCH,
  ACTION_CODES.UPDATE_CHURCH,
  ACTION_CODES.SYNC_CHURCH_REPORT_TEMPLATE,
  ACTION_CODES.BULK_ASSIGN_OVERRIDE_CHURCH_REPORT_TEMPLATE,
  ...BULK_TEMPLATE_ASSIGN_CODES,
];

const CHURCH_FILTER_FIELDS: FilterField[] = [
  NAME_SORT_FILTER_FIELD,
  { name: 'churchName',   label: 'Church Name',   type: 'text',   placeholder: 'Filter by church name', colWidth: 6 },
  { name: 'districtName', label: 'District Name', type: 'text',   placeholder: 'Filter by district name', colWidth: 6 },
  { name: 'isActive',     label: 'Status',        type: 'select', options: [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }], colWidth: 6 },
];

const CHURCH_LIST_CRITERIA_KEYS = ['freetext', 'churchName', 'districtName', 'isActive', 'districtId'] as const;

type ChurchListCriteria = {
  freetext?: string;
  churchName?: string;
  districtName?: string;
  isActive?: boolean;
  sortDir?: string;
};

function buildChurchListParams(
  criteria: ChurchListCriteria,
  routeDistrictId?: number,
): ChurchQueryParams {
  return withCatalogSort(
    buildDefaultListParams(
      {
        perPage: 100,
        page: 1,
        freetext: criteria.freetext || undefined,
        churchName: criteria.churchName || undefined,
        districtName: criteria.districtName || undefined,
        isActive: criteria.isActive,
        sortDir: normalizeSortDir(criteria.sortDir),
        ...(routeDistrictId ? { districtId: routeDistrictId } : {}),
      },
      [...CHURCH_LIST_CRITERIA_KEYS],
    ),
    SORT_BY_CHURCH_NAME
  );
}

const AllChurchesPage: React.FC = () => {
  const location = useLocation();
  const basePath = useEntityBasePath();
  const stateDistrictId = location.state?.districtId as number | undefined;

  // Seed search state from URL on first render
  const [urlParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => urlParams.get('q') ?? '');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const hasFilterFromUrl = !!(urlParams.get('q') || urlParams.get('churchName') || urlParams.get('districtName'));
  const routeDistrictId = hasFilterFromUrl ? undefined : stateDistrictId;
  const [filters, setFilters] = useState<Partial<ChurchQueryParams>>(() =>
    buildChurchListParams(
      {
        ...(urlParams.get('q') ? { freetext: urlParams.get('q')! } : {}),
        ...(urlParams.get('churchName') ? { churchName: urlParams.get('churchName')! } : {}),
        ...(urlParams.get('districtName') ? { districtName: urlParams.get('districtName')! } : {}),
        sortDir: readSortDirFromSearchParams(urlParams),
      },
      routeDistrictId,
    ),
  );
  const [currentPage, setCurrentPage] = useState(1);

  const scopedDistrictName = (filters.districtName ?? '').trim();
  const breadcrumbItems = useMemo(
    () =>
      basePath && scopedDistrictName
        ? districtsToFilteredChurchesTrail(basePath, scopedDistrictName)
        : [],
    [basePath, scopedDistrictName],
  );

  // Sync search state to URL
  useSearchUrlSync({
    query: debouncedSearchQuery,
    filters: {
      ...(filters.churchName ? { churchName: filters.churchName } : {}),
      ...(filters.districtName ? { districtName: filters.districtName } : {}),
      sortDir: normalizeSortDir(filters.sortDir),
    },
    page: 1,
  });
  const itemsPerPage = 10;

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState<Church | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewChurch, setViewChurch] = useState<Church | null>(null);
  const [formData, setFormData] = useState<Partial<Church>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [createFormErrors, setCreateFormErrors] = useState<CreateChurchFormErrors>({});
  const [showBulkModal, setShowBulkModal] = useState(false);
  const routeContextKey = `${location.pathname}:${routeDistrictId ?? 'all'}`;
  const previousRouteContextKeyRef = useRef(routeContextKey);
  const bulkSelection = useBulkSelection<Church>();

  const {
    churches,
    loading,
    error,
    refetch,
    updateParams,
    validationErrors
  } = useChurches(filters);

  const { districts, loading: districtsLoading } = useDistricts(
    withCatalogSort({ all: true, isActive: true, sortDir: SORT_DIR_ASC }, SORT_BY_DISTRICT_NAME)
  );

  // Nation admin context for the nation-scoped church enable/disable endpoints.
  const resolvedNationId = React.useMemo(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_ENTITY_CONTEXT);
      if (cached) {
        const ctx = JSON.parse(cached);
        if (ctx.scope === 'NATION') return ctx.scopeId as number;
      }
    } catch { /* ignore */ }
    return districts[0]?.nation_id ?? null;
  }, [districts]);

  const showError = useApiError();
  const { busy: statusBusy, doChurchDisable, doChurchEnable } = useChurchActions();
  const [statusConfirm, setStatusConfirm] = useState<{ church: Church; kind: StatusChangeKind } | null>(null);

  // ADMIN-only enable/disable gated on NATION-scoped action codes.
  const { hasPermission, hasAnyPermission } = usePermissions({
    scopeType: 'NATION',
    scopeId: resolvedNationId ?? undefined,
    actionCodes: CHURCH_ACTION_CODES,
  });
  const canCreate = hasPermission(ACTION_CODES.CREATE_CHURCH);
  const canDisable = hasPermission(ACTION_CODES.DISABLE_CHURCH);
  const canEnable = hasPermission(ACTION_CODES.ENABLE_CHURCH);
  const canEdit = hasPermission(ACTION_CODES.UPDATE_CHURCH);
  const canManageTemplates = hasPermission(ACTION_CODES.SYNC_CHURCH_REPORT_TEMPLATE);
  const canBulkAssign = hasAnyPermission(BULK_TEMPLATE_ASSIGN_CODES as unknown as string[]);

  const selectedChurchesForBulk = useMemo(
    () =>
      bulkSelection.getSelectedItems(churches, (c) => c.church_id).map((c) => ({
        id: c.church_id,
        name: c.church_name,
      })),
    [bulkSelection, churches]
  );

  const handleStatusConfirm = async () => {
    if (!statusConfirm) return;
    const { church, kind } = statusConfirm;
    const targetNationId = districts.find((d) => d.district_id === church.district_id)?.nation_id ?? resolvedNationId;
    if (!targetNationId) return;
    const response = kind === 'disable'
      ? await doChurchDisable(targetNationId, church.church_id)
      : await doChurchEnable(targetNationId, church.church_id);

    if (response.isSuccess()) {
      showSuccess(response.getData()?.message ?? `Church ${kind}d successfully.`);
      setStatusConfirm(null);
      refetch();
    } else {
      showError(response.getError() as ErrorResponse);
    }
  };

  // Do not auto-open create modal when viewing churches (e.g. from nation districts "View churches").
  // State district is still used to pre-fill the list filter and the create form when user clicks Create.

  const applyChurchListParams = (criteria: ChurchListCriteria) => {
    const next = buildChurchListParams(
      { ...criteria, sortDir: normalizeSortDir(criteria.sortDir ?? filters.sortDir) },
      routeDistrictId,
    );
    bulkSelection.deselectAll();
    setFilters(next);
    updateParams({
      perPage: 100,
      page: 1,
      freetext: criteria.freetext || undefined,
      churchName: criteria.churchName || undefined,
      districtName: criteria.districtName || undefined,
      isActive: criteria.isActive,
      districtId: routeDistrictId,
      all: next.all,
      sortBy: next.sortBy,
      sortDir: next.sortDir,
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    if (previousRouteContextKeyRef.current === routeContextKey) return;

    previousRouteContextKeyRef.current = routeContextKey;
    bulkSelection.deselectAll();
    setSearchQuery('');
    const next = buildChurchListParams({ sortDir: SORT_DIR_ASC }, routeDistrictId);
    setFilters(next);
    setCurrentPage(1);
    updateParams({
      perPage: 100,
      page: 1,
      freetext: undefined,
      churchName: undefined,
      districtName: undefined,
      isActive: undefined,
      districtId: routeDistrictId,
      all: next.all,
      sortBy: next.sortBy,
      sortDir: next.sortDir,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- clear selection only on route context change
  }, [routeContextKey, routeDistrictId, updateParams]);

  const handleCreateChurch = () => {
    setCreateFormErrors({});
    setFormData({
      church_name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      pastor: '',
      status: 'active',
      ...(routeDistrictId ? { district_id: routeDistrictId } : {}),
    });
    setShowCreateModal(true);
  };

  const showValidation = useErrorModalStore((s) => s.showValidation);

  const handleCreateSubmit = async () => {
    const messages: string[] = [];
    const errors: CreateChurchFormErrors = {};
    if (!formData.church_name?.trim()) {
      messages.push('Church name is required.');
      errors.church_name = ['Church name is required.'];
    }
    if (!formData.district_id) {
      messages.push('District is required.');
      errors.district_id = ['District is required.'];
    }
    if (messages.length > 0) {
      setCreateFormErrors(errors);
      showValidation(messages);
      return;
    }

    setFormLoading(true);
    setCreateFormErrors({});
    try {
      const districtId = formData.district_id as number;
      const churchName = formData.church_name as string;
      const response = await createChurch(districtId, {
        name: churchName,
        description: formData.description || '',
        alias: (formData as any).alias || undefined,
        established_at: formData.established_date || undefined,
      });
      if (response.isSuccess()) {
        setShowCreateModal(false);
        refetch();
      } else {
        const err = response.getError() as ErrorResponse | undefined;
        const fieldErrors = (err?.errors as CreateChurchFormErrors) ?? {};
        setCreateFormErrors(fieldErrors);
        const list = fieldErrors && typeof fieldErrors === 'object'
          ? Object.values(fieldErrors).flat().filter(Boolean)
          : [err?.detail || 'Failed to create church'];
        showValidation(list.length ? list : [err?.detail || 'Failed to create church']);
      }
    } catch (error) {
      console.error('Error creating church:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleReset = () => {
    applyChurchListParams({ sortDir: SORT_DIR_ASC });
  };

  const handleViewChurch = (church: Church) => {
    setViewChurch(church);
    setFormData({ ...church });
    setShowViewModal(true);
  };

  const handleUpdateChurch = async () => {
    const messages: string[] = [];
    if (!viewChurch) messages.push('No church selected.');
    if (!formData.church_name?.trim()) messages.push('Church name is required.');
    if (messages.length > 0) {
      showValidation(messages);
      return;
    }

    setFormLoading(true);
    try {
      const church = viewChurch!;
      const name = formData.church_name!;
      const response = await updateChurch(church.district_id, church.church_id, {
        name,
        description: formData.description || '',
        alias: (formData as any).alias || undefined,
        established_at: (formData as any).established_at || undefined,
      });
      if (response.isSuccess()) {
        setShowViewModal(false);
        refetch();
      } else {
        const error = response.getError();
        console.error('Error updating church:', error?.detail || 'Unknown error');
      }
    } catch (error) {
      console.error('Error updating church:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignTemplates = (church: any) => {
    setSelectedChurch(church);
    setShowTemplateModal(true);
  };

  const pagedChurches = churches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(churches.length / itemsPerPage));
  const { start, end } = getPaginationRange({
    page: currentPage,
    perPage: itemsPerPage,
    total: churches.length,
    currentCount: pagedChurches.length,
  });

  const tableColumns: DataTableColumn<Church>[] = [
    ...(canBulkAssign ? [{
      key: 'select',
      label: (
        <Form.Check
          type="checkbox"
          checked={pagedChurches.length > 0 && pagedChurches.every((c) => bulkSelection.isSelected(c.church_id))}
          onChange={() => bulkSelection.toggleAll(pagedChurches, (c) => c.church_id)}
          aria-label="Select all churches"
        />
      ) as any,
      width: '40px',
      render: (_value: any, row: Church) => (
        <Form.Check
          type="checkbox"
          checked={bulkSelection.isSelected(row.church_id)}
          onChange={(e) => {
            e.stopPropagation();
            bulkSelection.toggleItem(row.church_id);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.church_name}`}
        />
      ),
    }] : []),
    {
      key: 'church_name',
      label: 'Church Name',
      role: 'title',
      render: (_value: any, church: Church) => (
        <div>
          <strong>{church.church_name}</strong>
          {church.description && (
            <div className="text-muted small">
              {church.description}
            </div>
          )}
          {church.address && (
            <div className="text-muted small">
              <FiMapPin className="me-1" />
              {church.address}
            </div>
          )}
        </div>
      ),
      // On the card the title shows only the name (the title slot supplies the
      // weight — no <strong>); description renders in its own slot below.
      cardRender: (_value: any, church: Church) => church.church_name,
    },
    {
      key: 'description',
      label: 'Description',
      role: 'description',
      // Desktop already shows the description inside the name cell; this column
      // exists only to surface it as the card's wrapping paragraph.
      tableHidden: true,
      render: (_value: any, church: Church) => church.description || null,
    },
    {
      key: 'district_name',
      label: 'District',
      render: (_value: any, church: Church) => (
        <>{districts.find((d) => d.district_id === church.district_id)?.district_name || `District ${church.district_id}`}</>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      role: 'status',
      render: (_value: any, church: Church) => <StatusPill active={!!church.is_active} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      role: 'actions',
      width: '1%',
      className: 'text-end',
      render: (_value: any, church: Church) => (
        <TableActions aria-label={`Actions for church ${church.church_name}`}>
          <TableActionButton
            icon={canEdit ? <FiEdit2 aria-hidden="true" /> : <FiEye aria-hidden="true" />}
            label={canEdit ? 'Edit' : 'View'}
            primary
            onClick={() => handleViewChurch(church)}
          />
          <TableActionButton
            icon={canManageTemplates ? <FiFileText aria-hidden="true" /> : <FiEye aria-hidden="true" />}
            label={canManageTemplates ? 'Manage Templates' : 'View Templates'}
            primary
            variant="outline-secondary"
            onClick={() => handleAssignTemplates(church)}
          />
          {church.is_active && canDisable && (
            <TableActionButton
              icon={<FiSlash aria-hidden="true" />}
              label="Disable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--danger"
              onClick={() => setStatusConfirm({ church, kind: 'disable' })}
            />
          )}
          {!church.is_active && canEnable && (
            <TableActionButton
              icon={<FiCheckCircle aria-hidden="true" />}
              label="Enable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--success"
              onClick={() => setStatusConfirm({ church, kind: 'enable' })}
            />
          )}
        </TableActions>
      ),
    },
  ];

  return (
    <Container fluid className="py-4">
      <PageBreadcrumb items={breadcrumbItems} data-testid="nation-churches-breadcrumb" />
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">{scopedDistrictName ? `${scopedDistrictName} - Churches` : 'All Churches'}</h1>
          <p className="text-muted mb-0">{scopedDistrictName ? `View and manage churches in ${scopedDistrictName}` : 'View and manage all churches'}</p>
        </div>
        {canCreate && (
          <Button variant="primary" onClick={handleCreateChurch}>
            <FiPlus className="me-2" />
            Create Church
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search churches…"
          filterFields={CHURCH_FILTER_FIELDS}
          initialQuery={searchQuery}
          initialFilters={{
            churchName: filters.churchName,
            districtName: filters.districtName,
            isActive: filters.isActive === true ? '1' : filters.isActive === false ? '0' : undefined,
            sortDir: normalizeSortDir(filters.sortDir),
          }}
          onQueryChange={(q) => {
            setSearchQuery(q);
            applyChurchListParams({
              freetext: q.trim() || undefined,
              churchName: filters.churchName,
              districtName: filters.districtName,
              isActive: filters.isActive,
              sortDir: filters.sortDir,
            });
          }}
          onFiltersApply={(f) => {
            applyChurchListParams({
              // Keep the search box value; Clear filters only clears panel fields.
              freetext: searchQuery.trim() || undefined,
              churchName: f.churchName || undefined,
              districtName: f.districtName || undefined,
              isActive: f.isActive === '1' ? true : f.isActive === '0' ? false : undefined,
              sortDir: normalizeSortDir(f.sortDir),
            });
          }}
          onClear={() => { setSearchQuery(''); handleReset(); }}
          loading={loading}
          activeFilterCount={countContentFilters({
            churchName: filters.churchName,
            districtName: filters.districtName,
            isActive: filters.isActive !== undefined ? String(filters.isActive) : undefined,
            sortDir: filters.sortDir,
          })}
          filtersIgnored={debouncedSearchQuery.trim().length > 0 && [filters.churchName, filters.districtName].some(Boolean)}
          testId="churches-search"
        />
      </div>

      {/* Results */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <strong>Validation Errors:</strong>
          <ul className="mb-0 mt-2">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Churches Table */}
      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={pagedChurches}
            columns={tableColumns}
            rowNumberOffset={(currentPage - 1) * itemsPerPage}
            loading={loading}
            error={null}
            emptyMessage="No churches found"
            testId="all-churches-table"
            cardAriaLabel={(church) => `Church ${church.church_name}`}
            striped
            hover
            responsive
          />
        </Card.Body>
      </Card>

      {/* Pagination */}
      {churches.length > itemsPerPage && (
        <TablePaginationFooter
          className="mt-3"
          info={`Showing ${start} to ${end} of ${churches.length} churches`}
        >
          <PaginationBar
            page={currentPage}
            totalPages={totalPages}
            onPage={setCurrentPage}
          />
        </TablePaginationFooter>
      )}

      {/* View/Edit Church Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{canEdit ? 'Edit Church' : 'Church Details'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Church Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={formData.church_name || ''}
                onChange={(e) => setFormData({ ...formData, church_name: e.target.value })}
                required
                disabled={!canEdit}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={!canEdit}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Alias</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={(formData as any).alias || ''}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value } as any)}
                    placeholder="e.g. WPC"
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Established Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={(formData as any).established_at || ''}
                    onChange={(e) => setFormData({ ...formData, established_at: e.target.value } as any)}
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
          {canEdit && (
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                onClick={handleUpdateChurch}
                disabled={formLoading}
                aria-busy={formLoading}
              >
                {formLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Church'
                )}
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Create Church Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Church</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>District <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.district_id || ''}
                onChange={(e) => {
                  setFormData({ ...formData, district_id: parseInt(e.target.value) || undefined });
                  if (createFormErrors.district_id) setCreateFormErrors((prev) => ({ ...prev, district_id: undefined }));
                }}
                disabled={districtsLoading}
                isInvalid={!!createFormErrors.district_id}
              >
                <option value="">Select a district...</option>
                {districts.map((district) => (
                  <option key={district.district_id} value={district.district_id}>
                    {district.district_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{createFormErrors.district_id?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Church Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={formData.church_name || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, church_name: e.target.value });
                      if (createFormErrors.church_name) setCreateFormErrors((prev) => ({ ...prev, church_name: undefined }));
                    }}
                    required
                    isInvalid={!!createFormErrors.church_name}
                  />
                  <Form.Control.Feedback type="invalid">{createFormErrors.church_name?.[0]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Alias</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={(formData as any).alias || ''}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value } as any)}
                    placeholder="e.g. WPC"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                value={formData.description || ''}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (createFormErrors.description) setCreateFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                isInvalid={!!createFormErrors.description}
              />
              <Form.Control.Feedback type="invalid">{createFormErrors.description?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Established Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.established_date || ''}
                onChange={(e) => setFormData({ ...formData, established_date: e.target.value })}
              />
            </Form.Group>
          </Form>
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={handleCreateSubmit}
              disabled={formLoading}
              aria-busy={formLoading}
            >
              {formLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiPlus className="me-2" />
                  Create Church
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <ChurchTemplatesModal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        church={selectedChurch}
        districtId={selectedChurch?.district_id || 0}
        canManage={canManageTemplates}
        nationId={resolvedNationId ?? undefined}
      />

      {canBulkAssign && resolvedNationId && (
        <BulkAssignTemplatesModal
          show={showBulkModal}
          onHide={() => setShowBulkModal(false)}
          entities={selectedChurchesForBulk}
          entityType="church"
          nationId={resolvedNationId}
          onCompleted={() => {
            bulkSelection.deselectAll();
            refetch();
          }}
        />
      )}

      {canBulkAssign && bulkSelection.selectedCount > 0 && (
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow-lg rounded d-flex align-items-center gap-3"
          style={{ zIndex: 1050 }}
        >
          <Badge bg="primary">{bulkSelection.selectedCount} selected</Badge>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowBulkModal(true)}
            disabled={!resolvedNationId}
          >
            <FiCheckSquare className="me-2" />
            Bulk Assign Templates
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => bulkSelection.deselectAll()}
          >
            Clear Selection
          </Button>
        </div>
      )}

      <StatusChangeConfirmModal
        show={!!statusConfirm}
        kind={statusConfirm?.kind ?? 'disable'}
        entityLabel="church"
        entityName={statusConfirm?.church.church_name ?? ''}
        busy={statusBusy}
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusConfirm(null)}
        bodyText={statusConfirm?.kind === 'disable' ? (
          <>
            Are you sure you want to disable <strong>{statusConfirm?.church.church_name}</strong>? It can no longer submit reports and drops out of new-work selection lists. Historical data is unaffected.
          </>
        ) : undefined}
      />
    </Container>
  );
};

export default AllChurchesPage;
